import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaCheck, FaChevronDown, FaChevronUp, FaTrash, FaTimes, FaExclamationTriangle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

/**
 * PayrollCustomizationNew.jsx
 * - Mirrors ASPX PayrollCustomizationNew.aspx/.cs behavior
 * - Uses Node endpoints created earlier:
 *    GET    /api/payroll/customization
 *    GET    /api/payroll/customization/:schemeId
 *    POST   /api/payroll/customization/save
 *    DELETE /api/payroll/customization/:schemeId
 *
 * - Sends finYearId in header: x-fin-year-id
 * - Keeps the same "test calc" logic in React (client-side) like ASPX did
 *
 * IMPORTANT: Section/Row/Check/Text MUST live outside the page component.
 * Defining them inside PayrollCustomizationNew recreates the component type
 * on every keystroke and React remounts the input (focus is lost after 1 char).
 */

const Section = ({ title, children }) => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;

  return (
    <div
      className={`rounded-xl border overflow-hidden mb-6 ${
        isDark ? "border-gray-700 bg-gray-900/40" : "border-gray-200 bg-white"
      }`}
    >
      <div className={`${buttonClass} text-white px-4 py-3 font-semibold`}>{title}</div>
      <div className={`p-4 ${isDark ? "text-gray-200" : "text-gray-900"}`}>{children}</div>
    </div>
  );
};

const Row = ({ label, children, hint }) => {
  const { isDark } = useContext(ThemeContext);

  return (
    <div className="grid grid-cols-12 gap-3 items-center py-2">
      <div className="col-span-12 md:col-span-4">
        <div className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
          {label}
          {hint ? (
            <div className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{hint}</div>
          ) : null}
        </div>
      </div>
      <div className="col-span-12 md:col-span-8">{children}</div>
    </div>
  );
};

const Check = ({ checked, onChange, label }) => {
  const { isDark } = useContext(ThemeContext);

  return (
    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className={`w-4 h-4 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
      />
      <span className={`text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`}>{label}</span>
    </label>
  );
};

const Text = ({ value, onChange, placeholder, disabled, type = "text" }) => {
  const { isDark } = useContext(ThemeContext);

  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"
      } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      placeholder={placeholder}
    />
  );
};

const applyToggleSideEffects = (prev) => {
  const n = { ...prev };
  let changed = false;
  const setIf = (key, val) => {
    if (n[key] !== val) {
      n[key] = val;
      changed = true;
    }
  };

  if (!n.paidbasic) {
    setIf("paidbasicpercent", 0);
    setIf("pbnotes", "");
  }
  if (!n.conveyance) {
    setIf("conveyancepercent", 0);
    setIf("convnotes", "");
  }
  if (!n.educational) {
    setIf("educationalpercent", 0);
    setIf("edunotes", "");
  }
  if (!n.hra) {
    setIf("hrapercent", 0);
    setIf("hranotes", "");
  }

  if (!n.pf) {
    setIf("pfpercent", 0);
    setIf("pfnotes", "");
    setIf("pfgross", false);
    setIf("pfgrosspercent", 0);
    setIf("pfgrossnotes", "");
    setIf("pfpb", false);
    setIf("pfpbpercent", 0);
    setIf("pfpbnotes", "");
    setIf("pfcon", false);
    setIf("pfconpercent", 0);
    setIf("pfconnotes", "");
    setIf("pfedu", false);
    setIf("pfedupercent", 0);
    setIf("pfedunotes", "");
    setIf("pfhra", false);
    setIf("pfhrapercent", 0);
    setIf("pfhranotes", "");
    setIf("pftotpercent", 0);
    setIf("pftotnotes", "");
    setIf("pfgreater", false);
    setIf("pfgreatervalue", 0);
    setIf("pfgreaternotes", "");
  }

  if (n.pf && n.pfgross) {
    setIf("pfpb", false);
    setIf("pfpbpercent", 0);
    setIf("pfpbnotes", "");
    setIf("pfcon", false);
    setIf("pfconpercent", 0);
    setIf("pfconnotes", "");
    setIf("pfedu", false);
    setIf("pfedupercent", 0);
    setIf("pfedunotes", "");
    setIf("pfhra", false);
    setIf("pfhrapercent", 0);
    setIf("pfhranotes", "");
    setIf("pftotpercent", 0);
  }

  if (!n.esi) {
    setIf("esipercent", 0);
    setIf("esinotes", "");
    setIf("esigross", false);
    setIf("esigrosspercent", 0);
    setIf("esigrossnotes", "");
    setIf("esipb", false);
    setIf("esipbpercent", 0);
    setIf("esipbnotes", "");
    setIf("esicon", false);
    setIf("esiconpercent", 0);
    setIf("esiconnotes", "");
    setIf("esiedu", false);
    setIf("esiedupercent", 0);
    setIf("esiedunotes", "");
    setIf("esihra", false);
    setIf("esihrapercent", 0);
    setIf("esihranotes", "");
    setIf("esitotpercent", 0);
    setIf("esitotnotes", "");
    setIf("esigreater", false);
    setIf("esigreatervalue", 0);
    setIf("esigreaternotes", "");
  }

  if (n.esi && n.esigross) {
    setIf("esipb", false);
    setIf("esipbpercent", 0);
    setIf("esipbnotes", "");
    setIf("esicon", false);
    setIf("esiconpercent", 0);
    setIf("esiconnotes", "");
    setIf("esiedu", false);
    setIf("esiedupercent", 0);
    setIf("esiedunotes", "");
    setIf("esihra", false);
    setIf("esihrapercent", 0);
    setIf("esihranotes", "");
    setIf("esitotpercent", 0);
  }

  if (!n.pfgreater) {
    setIf("pfgreatervalue", 0);
    setIf("pfgreaternotes", "");
  }
  if (!n.esigreater) {
    setIf("esigreatervalue", 0);
    setIf("esigreaternotes", "");
  }

  if (!n.netpay) {
    setIf("netpaypb", false);
    setIf("netpaycon", false);
    setIf("netpayedu", false);
    setIf("netpayhra", false);
    setIf("netpaypf", false);
    setIf("netpayesi", false);
    setIf("netpayadvance", false);
    setIf("netpayloan", false);
    setIf("netpaytds", false);
  }

  return changed ? n : prev;
};

const TOGGLE_FIELDS = new Set([
  "paidbasic",
  "conveyance",
  "educational",
  "hra",
  "pf",
  "pfgross",
  "esi",
  "esigross",
  "pfgreater",
  "esigreater",
  "netpay",
]);

const PayrollCustomizationNew = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  // UI
  const [showForm, setShowForm] = useState(true);
  const [saving, setSaving] = useState(false);

  // Listing
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  // Edit mode
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  // Selected for bulk delete
  const selectedIds = useMemo(() => records.filter((r) => r.selected).map((r) => r.id), [records]);

  // Confirm dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [deleteMode, setDeleteMode] = useState("bulk"); // "one" | "bulk"

  // ---------------------------------
  // Helpers
  // ---------------------------------
  const finYearId = Number(localStorage.getItem("finYearId") || 0);

  const api = axios.create({
    baseURL: BASE_URL,
    headers: {
      "x-fin-year-id": finYearId || "",
    },
  });

  const bit = (v) => (v === true ? 1 : 0);
  const toNum = (v, def = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : def;
  };

  const calcPercentValue = (percent, gross) => (toNum(percent) * toNum(gross)) / 100;

  // ---------------------------------
  // Form State
  // ---------------------------------
  const initialForm = {
    schemename: "",
    schemenotes: "",
    enable: false,

    grosstest: 16000,

    // Main components
    paidbasic: false,
    paidbasicpercent: 0,
    pbnotes: "",

    conveyance: false,
    conveyancepercent: 0,
    convnotes: "",

    educational: false,
    educationalpercent: 0,
    edunotes: "",

    hra: false,
    hrapercent: 0,
    hranotes: "",

    // PF
    pf: false,
    pfpercent: 0,
    pfnotes: "",

    pfgross: false,
    pfgrosspercent: 0,
    pfgrossnotes: "",

    pfpb: false,
    pfpbpercent: 0,
    pfpbnotes: "",

    pfcon: false,
    pfconpercent: 0,
    pfconnotes: "",

    pfedu: false,
    pfedupercent: 0,
    pfedunotes: "",

    pfhra: false,
    pfhrapercent: 0,
    pfhranotes: "",

    pftotpercent: 0,
    pftotnotes: "",

    pfgreater: false,
    pfgreatervalue: 0,
    pfgreaternotes: "",

    // ESI
    esi: false,
    esipercent: 0,
    esinotes: "",

    esigross: false,
    esigrosspercent: 0,
    esigrossnotes: "",

    esipb: false,
    esipbpercent: 0,
    esipbnotes: "",

    esicon: false,
    esiconpercent: 0,
    esiconnotes: "",

    esiedu: false,
    esiedupercent: 0,
    esiedunotes: "",

    esihra: false,
    esihrapercent: 0,
    esihranotes: "",

    esitotpercent: 0,
    esitotnotes: "",

    esigreater: false,
    esigreatervalue: 0,
    esigreaternotes: "",

    // Others
    advance: false,
    loan: false,
    tds: false,

    // Netpay
    netpay: false,
    netpaypb: false,
    netpaycon: false,
    netpayedu: false,
    netpayhra: false,
    netpaypf: false,
    netpayesi: false,
    netpayadvance: false,
    netpayloan: false,
    netpaytds: false,
  };

  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});

  // ---------------------------------
  // Derived “Test” Values (like ASPX labels)
  // ---------------------------------
  const tests = useMemo(() => {
    const g = toNum(formData.grosstest);

    const pb = calcPercentValue(formData.paidbasicpercent, g);
    const con = calcPercentValue(formData.conveyancepercent, g);
    const edu = calcPercentValue(formData.educationalpercent, g);
    const hra = calcPercentValue(formData.hrapercent, g);

    // PF base parts
    const pfgrossVal = calcPercentValue(formData.pfgrosspercent, g);
    const pfpbVal = calcPercentValue(formData.pfpbpercent, g);
    const pfconVal = calcPercentValue(formData.pfconpercent, g);
    const pfeduVal = calcPercentValue(formData.pfedupercent, g);
    const pfhraVal = calcPercentValue(formData.pfhrapercent, g);

    // ESI base parts
    const esigrossVal = calcPercentValue(formData.esigrosspercent, g);
    const esipbVal = calcPercentValue(formData.esipbpercent, g);
    const esiconVal = calcPercentValue(formData.esiconpercent, g);
    const esieduVal = calcPercentValue(formData.esiedupercent, g);
    const esihraVal = calcPercentValue(formData.esihrapercent, g);

    // PF total base
    let pfBase = 0;
    if (formData.pfgross) {
      pfBase = pfgrossVal;
    } else {
      const sum = pfpbVal + pfconVal + pfeduVal + pfhraVal;
      pfBase = (sum * toNum(formData.pftotpercent)) / 100;
    }

    // Apply PF greater cap logic (aspx style)
    let pfFinal = (pfBase * toNum(formData.pfpercent)) / 100;
    if (formData.pfgreater) {
      const gv = toNum(formData.pfgreatervalue);
      if (pfBase >= gv) {
        pfFinal = (gv * toNum(formData.pfpercent)) / 100;
      }
    }

    // ESI total base
    let esiBase = 0;
    if (formData.esigross) {
      esiBase = esigrossVal;
    } else {
      const sum = esipbVal + esiconVal + esieduVal + esihraVal;
      esiBase = (sum * toNum(formData.esitotpercent)) / 100;
    }

    // Apply ESI greater cap logic (aspx style)
    let esiFinal = (esiBase * toNum(formData.esipercent)) / 100;
    if (formData.esigreater) {
      const gv = toNum(formData.esigreatervalue);
      if (esiBase >= gv) {
        esiFinal = (gv * toNum(formData.esipercent)) / 100;
      }
    }

    // Netpay calc (ASPX did: add selected earnings, minus selected deductions)
    let netpayVal = 0;
    if (formData.netpaypb) netpayVal += pb;
    if (formData.netpaycon) netpayVal += con;
    if (formData.netpayedu) netpayVal += edu;
    if (formData.netpayhra) netpayVal += hra;

    if (formData.netpaypf) netpayVal -= pfFinal;
    if (formData.netpayesi) netpayVal -= esiFinal;

    // Advance/loan/tds not computed here because ASPX only toggled them (no percent)
    return {
      pb,
      con,
      edu,
      hra,
      pfBase,
      pfFinal,
      esiBase,
      esiFinal,
      netpayVal,
    };
  }, [formData]);

  // Toggle side-effects run in setField() only when a checkbox changes.


  // ---------------------------------
  // Validations (like ASPX TextChanged checks)
  // ---------------------------------
  const validateForm = () => {
    const e = {};
    if (!String(formData.schemename || "").trim()) e.schemename = "Scheme name is required";

    const clamp100 = (val, key) => {
      if (toNum(val) > 100) e[key] = "Enter less than or equal to 100";
      if (toNum(val) < 0) e[key] = "Enter valid percentage";
    };

    // Main component percent total <= 100 (only for checked)
    const mainSum =
      (formData.paidbasic ? toNum(formData.paidbasicpercent) : 0) +
      (formData.conveyance ? toNum(formData.conveyancepercent) : 0) +
      (formData.educational ? toNum(formData.educationalpercent) : 0) +
      (formData.hra ? toNum(formData.hrapercent) : 0);

    if (mainSum > 100) e.mainTotal = "Paid Basic + Conveyance + Educational + HRA should be <= 100%";

    // Clamp some key percents
    clamp100(formData.paidbasicpercent, "paidbasicpercent");
    clamp100(formData.conveyancepercent, "conveyancepercent");
    clamp100(formData.educationalpercent, "educationalpercent");
    clamp100(formData.hrapercent, "hrapercent");

    if (formData.pf) {
      clamp100(formData.pfpercent, "pfpercent");
      clamp100(formData.pfgrosspercent, "pfgrosspercent");
      clamp100(formData.pftotpercent, "pftotpercent");

      // PF split sum <= 100 (only when not gross)
      if (!formData.pfgross) {
        const pfSum =
          (formData.pfpb ? toNum(formData.pfpbpercent) : 0) +
          (formData.pfcon ? toNum(formData.pfconpercent) : 0) +
          (formData.pfedu ? toNum(formData.pfedupercent) : 0) +
          (formData.pfhra ? toNum(formData.pfhrapercent) : 0);
        if (pfSum > 100) e.pfSplitTotal = "PF split total should be <= 100%";
      }
    }

    if (formData.esi) {
      clamp100(formData.esipercent, "esipercent");
      clamp100(formData.esigrosspercent, "esigrosspercent");
      clamp100(formData.esitotpercent, "esitotpercent");

      if (!formData.esigross) {
        const esiSum =
          (formData.esipb ? toNum(formData.esipbpercent) : 0) +
          (formData.esicon ? toNum(formData.esiconpercent) : 0) +
          (formData.esiedu ? toNum(formData.esiedupercent) : 0) +
          (formData.esihra ? toNum(formData.esihrapercent) : 0);
        if (esiSum > 100) e.esiSplitTotal = "ESI split total should be <= 100%";
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ---------------------------------
  // LIST API (FillUserSetup)
  // ---------------------------------
  const fetchList = async () => {
    if (!finYearId) {
      toast.error("finYearId missing! Please login again.");
      return;
    }

    try {
      setLoading(true);
      const res = await api.get("/api/payroll/customization");

      if (!res.data?.status) {
        toast.error(res.data?.message || "Failed to load list");
        return;
      }

      const list = res.data.data || [];
      const mapped = list.map((row, idx) => ({
        id: row.fld_schemeid,
        sNo: idx + 1,
        schemename: row.fld_schemename,
        paidbasicpercent: row.fld_paidbasicpercent,
        conveyancepercent: row.fld_conveyancepercent,
        educationalpercent: row.fld_educationalpercent,
        hrapercent: row.fld_hrapercent,
        pfpercent: row.fld_pfpercent,
        esipercent: row.fld_esipercent,
        enable: !!row.fld_enable,
        notes: row.fld_schemenotes || "",
        selected: false,
      }));

      setRecords(mapped);
    } catch (err) {
      console.error("List error:", err);
      toast.error(err.response?.data?.message || "Failed to load list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------
  // EDIT (RowCommand View)
  // ---------------------------------
  const handleEditById = async (schemeId) => {
    if (!finYearId) {
      toast.error("finYearId missing! Please login again.");
      return;
    }

    try {
      const res = await api.get(`/api/payroll/customization/${schemeId}`);

      if (!res.data?.status) {
        toast.error(res.data?.message || "Failed to fetch record");
        return;
      }

      const d = res.data.data;
      if (!d) {
        toast.error("Record not found");
        return;
      }

      // Map DB -> form (DB fields are already close to our keys)
      setFormData((prev) => ({
        ...prev,
        schemename: d.fld_schemename || "",
        schemenotes: d.fld_schemenotes || "",
        enable: d.fld_enable === 1 || d.fld_enable === true,

        paidbasic: d.fld_paidbasic === 1 || d.fld_paidbasic === true,
        paidbasicpercent: toNum(d.fld_paidbasicpercent),
        pbnotes: d.fld_pbnotes || "",

        conveyance: d.fld_conveyance === 1 || d.fld_conveyance === true,
        conveyancepercent: toNum(d.fld_conveyancepercent),
        convnotes: d.fld_convnotes || "",

        educational: d.fld_educational === 1 || d.fld_educational === true,
        educationalpercent: toNum(d.fld_educationalpercent),
        edunotes: d.fld_edunotes || "",

        hra: d.fld_hra === 1 || d.fld_hra === true,
        hrapercent: toNum(d.fld_hrapercent),
        hranotes: d.fld_hranotes || "",

        pf: d.fld_pf === 1 || d.fld_pf === true,
        pfpercent: toNum(d.fld_pfpercent),
        pfnotes: d.fld_pfnotes || "",

        pfgross: d.fld_pfgross === 1 || d.fld_pfgross === true,
        pfgrosspercent: toNum(d.fld_pfgrosspercent),
        pfgrossnotes: d.fld_pfgrossnotes || "",

        pfpb: d.fld_pfpb === 1 || d.fld_pfpb === true,
        pfpbpercent: toNum(d.fld_pfpbpercent),
        pfpbnotes: d.fld_pfpbnotes || "",

        pfcon: d.fld_pfcon === 1 || d.fld_pfcon === true,
        pfconpercent: toNum(d.fld_pfconpercent),
        pfconnotes: d.fld_pfconnotes || "",

        pfedu: d.fld_pfedu === 1 || d.fld_pfedu === true,
        pfedupercent: toNum(d.fld_pfedupercent),
        pfedunotes: d.fld_pfedunotes || "",

        pfhra: d.fld_pfhra === 1 || d.fld_pfhra === true,
        pfhrapercent: toNum(d.fld_pfhrapercent),
        pfhranotes: d.fld_pfhranotes || "",

        pftotpercent: toNum(d.fld_pftotpercent),
        pftotnotes: d.fld_pftotnotes || "",

        pfgreater: d.fld_pfgreater === 1 || d.fld_pfgreater === true,
        pfgreatervalue: toNum(d.fld_pfgreatervalue),
        pfgreaternotes: d.fld_pfgreaternotes || "",

        esi: d.fld_esi === 1 || d.fld_esi === true,
        esipercent: toNum(d.fld_esipercent),
        esinotes: d.fld_esinotes || "",

        esigross: d.fld_esigross === 1 || d.fld_esigross === true,
        esigrosspercent: toNum(d.fld_esigrosspercent),
        esigrossnotes: d.fld_esigrossnotes || "",

        esipb: d.fld_esipb === 1 || d.fld_esipb === true,
        esipbpercent: toNum(d.fld_esipbpercent),
        esipbnotes: d.fld_esipbnotes || "",

        esicon: d.fld_esicon === 1 || d.fld_esicon === true,
        esiconpercent: toNum(d.fld_esiconpercent),
        esiconnotes: d.fld_esiconnotes || "",

        esiedu: d.fld_esiedu === 1 || d.fld_esiedu === true,
        esiedupercent: toNum(d.fld_esiedupercent),
        esiedunotes: d.fld_esiedunotes || "",

        esihra: d.fld_esihra === 1 || d.fld_esihra === true,
        esihrapercent: toNum(d.fld_esihrapercent),
        esihranotes: d.fld_esihranotes || "",

        esitotpercent: toNum(d.fld_esitotpercent),
        esitotnotes: d.fld_esitotnotes || "",

        esigreater: d.fld_esigreater === 1 || d.fld_esigreater === true,
        esigreatervalue: toNum(d.fld_esigreatervalue),
        esigreaternotes: d.fld_esigreaternotes || "",

        advance: d.fld_advance === 1 || d.fld_advance === true,
        loan: d.fld_loan === 1 || d.fld_loan === true,
        tds: d.fld_tds === 1 || d.fld_tds === true,

        netpay: d.fld_netpay === 1 || d.fld_netpay === true,
        netpaypb: d.fld_netpaypb === 1 || d.fld_netpaypb === true,
        netpaycon: d.fld_netpaycon === 1 || d.fld_netpaycon === true,
        netpayedu: d.fld_netpayedu === 1 || d.fld_netpayedu === true,
        netpayhra: d.fld_netpayhra === 1 || d.fld_netpayhra === true,
        netpaypf: d.fld_netpaypf === 1 || d.fld_netpaypf === true,
        netpayesi: d.fld_netpayesi === 1 || d.fld_netpayesi === true,
        netpayadvance: d.fld_netpayadvance === 1 || d.fld_netpayadvance === true,
        netpayloan: d.fld_netpayloan === 1 || d.fld_netpayloan === true,
        netpaytds: d.fld_netpaytds === 1 || d.fld_netpaytds === true,
      }));

      setIsEdit(true);
      setEditId(Number(schemeId));
      setShowForm(true);
      setErrors({});
      toast.info("Loaded scheme for editing");
    } catch (err) {
      console.error("Edit fetch error:", err);
      toast.error(err.response?.data?.message || "Server error");
    }
  };

  // ---------------------------------
  // SUBMIT (btn_save_Click)
  // ---------------------------------
  const handleSubmit = async () => {
    if (!finYearId) {
      toast.error("finYearId missing! Please login again.");
      return;
    }

    if (!validateForm()) {
      toast.error("Please fix validation errors");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...(isEdit && editId ? { schemeId: editId } : {}),
        schemename: String(formData.schemename || "").trim(),
        schemenotes: formData.schemenotes || "",
        enable: !!formData.enable,

        grosstest: toNum(formData.grosstest), // not saved in DB, ok to send; server ignores unknowns if you want

        paidbasic: !!formData.paidbasic,
        paidbasicpercent: toNum(formData.paidbasicpercent),
        pbnotes: formData.pbnotes || "",

        conveyance: !!formData.conveyance,
        conveyancepercent: toNum(formData.conveyancepercent),
        convnotes: formData.convnotes || "",

        educational: !!formData.educational,
        educationalpercent: toNum(formData.educationalpercent),
        edunotes: formData.edunotes || "",

        hra: !!formData.hra,
        hrapercent: toNum(formData.hrapercent),
        hranotes: formData.hranotes || "",

        pf: !!formData.pf,
        pfpercent: toNum(formData.pfpercent),
        pfnotes: formData.pfnotes || "",

        pfgross: !!formData.pfgross,
        pfgrosspercent: toNum(formData.pfgrosspercent),
        pfgrossnotes: formData.pfgrossnotes || "",

        pfpb: !!formData.pfpb,
        pfpbpercent: toNum(formData.pfpbpercent),
        pfpbnotes: formData.pfpbnotes || "",

        pfcon: !!formData.pfcon,
        pfconpercent: toNum(formData.pfconpercent),
        pfconnotes: formData.pfconnotes || "",

        pfedu: !!formData.pfedu,
        pfedupercent: toNum(formData.pfedupercent),
        pfedunotes: formData.pfedunotes || "",

        pfhra: !!formData.pfhra,
        pfhrapercent: toNum(formData.pfhrapercent),
        pfhranotes: formData.pfhranotes || "",

        pftotpercent: toNum(formData.pftotpercent),
        pftotnotes: formData.pftotnotes || "",

        pfgreater: !!formData.pfgreater,
        pfgreatervalue: toNum(formData.pfgreatervalue),
        pfgreaternotes: formData.pfgreaternotes || "",

        esi: !!formData.esi,
        esipercent: toNum(formData.esipercent),
        esinotes: formData.esinotes || "",

        esigross: !!formData.esigross,
        esigrosspercent: toNum(formData.esigrosspercent),
        esigrossnotes: formData.esigrossnotes || "",

        esipb: !!formData.esipb,
        esipbpercent: toNum(formData.esipbpercent),
        esipbnotes: formData.esipbnotes || "",

        esicon: !!formData.esicon,
        esiconpercent: toNum(formData.esiconpercent),
        esiconnotes: formData.esiconnotes || "",

        esiedu: !!formData.esiedu,
        esiedupercent: toNum(formData.esiedupercent),
        esiedunotes: formData.esiedunotes || "",

        esihra: !!formData.esihra,
        esihrapercent: toNum(formData.esihrapercent),
        esihranotes: formData.esihranotes || "",

        esitotpercent: toNum(formData.esitotpercent),
        esitotnotes: formData.esitotnotes || "",

        esigreater: !!formData.esigreater,
        esigreatervalue: toNum(formData.esigreatervalue),
        esigreaternotes: formData.esigreaternotes || "",

        advance: !!formData.advance,
        loan: !!formData.loan,
        tds: !!formData.tds,

        netpay: !!formData.netpay,
        netpaypb: !!formData.netpaypb,
        netpaycon: !!formData.netpaycon,
        netpayedu: !!formData.netpayedu,
        netpayhra: !!formData.netpayhra,
        netpaypf: !!formData.netpaypf,
        netpayesi: !!formData.netpayesi,
        netpayadvance: !!formData.netpayadvance,
        netpayloan: !!formData.netpayloan,
        netpaytds: !!formData.netpaytds,
      };

      const res = await api.post("/api/payroll/customization/save", payload);

      if (!res.data?.status) {
        toast.error(res.data?.message || "Save failed");
        return;
      }

      toast.success(res.data.message || (isEdit ? "Updated successfully" : "Saved successfully"));
      await fetchList();
      handleAddNew(true);
    } catch (err) {
      console.error("Submit Error:", err);
      toast.error(err.response?.data?.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------
  // Clear / New
  // ---------------------------------
  const handleAddNew = (silent = false) => {
    setIsEdit(false);
    setEditId(null);
    setErrors({});
    setFormData((prev) => ({
      ...initialForm,
      grosstest: prev.grosstest || 16000, // keep your last test gross
    }));
    if (!silent) toast.info("Form cleared for new entry");
  };

  // ---------------------------------
  // Single / bulk delete confirm
  // ---------------------------------
  const openDeleteOne = (schemeId) => {
    setDeleteMode("one");
    setPendingDeleteIds([schemeId]);
    setConfirmOpen(true);
  };

  const openRemoveSelected = () => {
    if (selectedIds.length === 0) {
      toast.error("Please select records to remove");
      return;
    }
    setDeleteMode("bulk");
    setPendingDeleteIds(selectedIds);
    setConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (isDeleting) return;
    setConfirmOpen(false);
    setPendingDeleteIds([]);
  };

  const confirmDeleteSelected = async () => {
    if (pendingDeleteIds.length === 0) return;

    try {
      setIsDeleting(true);

      for (const id of pendingDeleteIds) {
        const res = await api.delete(`/api/payroll/customization/${id}`);
        if (!res.data?.status) {
          toast.error(res.data?.message || `Delete failed for scheme ${id}`);
          await fetchList();
          return;
        }
      }

      toast.success(
        deleteMode === "one" ? "Deleted successfully" : "Deleted selected schemes"
      );
      setConfirmOpen(false);
      setPendingDeleteIds([]);
      await fetchList();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.response?.data?.message || "Server error");
    } finally {
      setIsDeleting(false);
    }
  };

  // ---------------------------------
  // Form input handlers
  // ---------------------------------
  const setField = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      return TOGGLE_FIELDS.has(field) ? applyToggleSideEffects(next) : next;
    });
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const onPercent = (field, value) => {
    // Keep empty while typing so the field does not jump to 0 mid-entry.
    if (value === "" || value === "-" || value === ".") {
      setField(field, value);
      return;
    }
    const n = Number(value);
    setField(field, Number.isFinite(n) ? n : 0);
  };

  const handleSelectRecord = (id, checked) => {
    setRecords((prev) => prev.map((r) => (r.id === id ? { ...r, selected: checked } : r)));
  };

  // ---------------------------------
  // Render
  // ---------------------------------
  return (
    <AdminLayoutWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">Payroll Customization</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Top bar */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Scheme Setup
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {records.length} Records
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate("/adminMaster/home")}
                className={`px-4 py-2 rounded-lg font-semibold shadow ${
                  isDark ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                }`}
              >
                Back
              </button>

              <button
                onClick={() => setShowForm(!showForm)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showForm ? <FaChevronUp /> : <FaChevronDown />}
                {showForm ? "Hide Entry Form" : "Show Entry Form"}
              </button>
            </div>
          </div>

          {/* Form */}
          {showForm && (
            <>
              <Section title={isEdit ? "Edit Scheme" : "New Scheme"}>
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12 md:col-span-6">
                    <Row label="Scheme Name" hint={errors.schemename || errors.mainTotal}>
                      <Text
                        value={formData.schemename}
                        onChange={(v) => setField("schemename", v)}
                        placeholder="Enter Scheme Name..."
                        disabled={saving}
                      />
                      {(errors.schemename || errors.mainTotal) && (
                        <div className="text-red-400 text-xs mt-1">{errors.schemename || errors.mainTotal}</div>
                      )}
                    </Row>
                  </div>
                  <div className="col-span-12 md:col-span-6">
                    <Row label="Notes">
                      <Text
                        value={formData.schemenotes}
                        onChange={(v) => setField("schemenotes", v)}
                        placeholder="Optional notes..."
                        disabled={saving}
                      />
                    </Row>
                  </div>

                  <div className="col-span-12 md:col-span-6">
                    <Row label="Gross Salary For Test">
                      <Text
                        type="number"
                        value={formData.grosstest}
                        onChange={(v) => setField("grosstest", toNum(v))}
                        placeholder="16000"
                        disabled={saving}
                      />
                    </Row>
                  </div>

                  <div className="col-span-12 md:col-span-6">
                    <Row label="Enable">
                      <Check checked={formData.enable} onChange={(v) => setField("enable", v)} label="Enable Scheme" />
                    </Row>
                  </div>
                </div>
              </Section>

              <Section title="Earnings (Percentage from Gross Pay - 100%)">
                <div className="grid grid-cols-12 gap-4">
                  {/* Paid Basic */}
                  <div className="col-span-12 md:col-span-6">
                    <Row label="Paid Basic">
                      <div className="flex flex-col gap-2">
                        <Check
                          checked={formData.paidbasic}
                          onChange={(v) => setField("paidbasic", v)}
                          label="Enable Paid Basic"
                        />
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-6">
                            <Text
                              type="number"
                              value={formData.paidbasicpercent}
                              onChange={(v) => onPercent("paidbasicpercent", v)}
                              placeholder="0"
                              disabled={!formData.paidbasic || saving}
                            />
                            {errors.paidbasicpercent && (
                              <div className="text-red-400 text-xs mt-1">{errors.paidbasicpercent}</div>
                            )}
                          </div>
                          <div className="col-span-6 text-sm">
                            Test: <span className="font-semibold">{tests.pb.toFixed(2)}</span>
                          </div>
                        </div>
                        <Text
                          value={formData.pbnotes}
                          onChange={(v) => setField("pbnotes", v)}
                          placeholder="Notes..."
                          disabled={!formData.paidbasic || saving}
                        />
                      </div>
                    </Row>
                  </div>

                  {/* Conveyance */}
                  <div className="col-span-12 md:col-span-6">
                    <Row label="Conveyance">
                      <div className="flex flex-col gap-2">
                        <Check
                          checked={formData.conveyance}
                          onChange={(v) => setField("conveyance", v)}
                          label="Enable Conveyance"
                        />
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-6">
                            <Text
                              type="number"
                              value={formData.conveyancepercent}
                              onChange={(v) => onPercent("conveyancepercent", v)}
                              placeholder="0"
                              disabled={!formData.conveyance || saving}
                            />
                            {errors.conveyancepercent && (
                              <div className="text-red-400 text-xs mt-1">{errors.conveyancepercent}</div>
                            )}
                          </div>
                          <div className="col-span-6 text-sm">
                            Test: <span className="font-semibold">{tests.con.toFixed(2)}</span>
                          </div>
                        </div>
                        <Text
                          value={formData.convnotes}
                          onChange={(v) => setField("convnotes", v)}
                          placeholder="Notes..."
                          disabled={!formData.conveyance || saving}
                        />
                      </div>
                    </Row>
                  </div>

                  {/* Educational */}
                  <div className="col-span-12 md:col-span-6">
                    <Row label="Educational">
                      <div className="flex flex-col gap-2">
                        <Check
                          checked={formData.educational}
                          onChange={(v) => setField("educational", v)}
                          label="Enable Educational"
                        />
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-6">
                            <Text
                              type="number"
                              value={formData.educationalpercent}
                              onChange={(v) => onPercent("educationalpercent", v)}
                              placeholder="0"
                              disabled={!formData.educational || saving}
                            />
                            {errors.educationalpercent && (
                              <div className="text-red-400 text-xs mt-1">{errors.educationalpercent}</div>
                            )}
                          </div>
                          <div className="col-span-6 text-sm">
                            Test: <span className="font-semibold">{tests.edu.toFixed(2)}</span>
                          </div>
                        </div>
                        <Text
                          value={formData.edunotes}
                          onChange={(v) => setField("edunotes", v)}
                          placeholder="Notes..."
                          disabled={!formData.educational || saving}
                        />
                      </div>
                    </Row>
                  </div>

                  {/* HRA */}
                  <div className="col-span-12 md:col-span-6">
                    <Row label="HRA">
                      <div className="flex flex-col gap-2">
                        <Check checked={formData.hra} onChange={(v) => setField("hra", v)} label="Enable HRA" />
                        <div className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-6">
                            <Text
                              type="number"
                              value={formData.hrapercent}
                              onChange={(v) => onPercent("hrapercent", v)}
                              placeholder="0"
                              disabled={!formData.hra || saving}
                            />
                            {errors.hrapercent && <div className="text-red-400 text-xs mt-1">{errors.hrapercent}</div>}
                          </div>
                          <div className="col-span-6 text-sm">
                            Test: <span className="font-semibold">{tests.hra.toFixed(2)}</span>
                          </div>
                        </div>
                        <Text
                          value={formData.hranotes}
                          onChange={(v) => setField("hranotes", v)}
                          placeholder="Notes..."
                          disabled={!formData.hra || saving}
                        />
                      </div>
                    </Row>
                  </div>
                </div>
              </Section>

              <Section title="PF (Settings)">
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12">
                    <Row label="PF Enable">
                      <Check checked={formData.pf} onChange={(v) => setField("pf", v)} label="Enable PF" />
                    </Row>
                  </div>

                  {formData.pf && (
                    <>
                      <div className="col-span-12 md:col-span-6">
                        <Row label="PF %">
                          <Text
                            type="number"
                            value={formData.pfpercent}
                            onChange={(v) => onPercent("pfpercent", v)}
                            placeholder="0"
                            disabled={saving}
                          />
                          {errors.pfpercent && <div className="text-red-400 text-xs mt-1">{errors.pfpercent}</div>}
                        </Row>
                      </div>

                      <div className="col-span-12 md:col-span-6">
                        <Row label="PF Notes">
                          <Text value={formData.pfnotes} onChange={(v) => setField("pfnotes", v)} placeholder="Notes..." />
                        </Row>
                      </div>

                      <div className="col-span-12 md:col-span-6">
                        <Row label="PF Base From Gross">
                          <Check
                            checked={formData.pfgross}
                            onChange={(v) => setField("pfgross", v)}
                            label="Gross"
                          />
                          <div className="mt-2 grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-6">
                              <Text
                                type="number"
                                value={formData.pfgrosspercent}
                                onChange={(v) => onPercent("pfgrosspercent", v)}
                                placeholder="0"
                                disabled={!formData.pfgross || saving}
                              />
                              {errors.pfgrosspercent && (
                                <div className="text-red-400 text-xs mt-1">{errors.pfgrosspercent}</div>
                              )}
                            </div>
                            <div className="col-span-6 text-sm">
                              Base: <span className="font-semibold">{tests.pfBase.toFixed(2)}</span>
                            </div>
                          </div>
                          <div className="mt-2">
                            <Text
                              value={formData.pfgrossnotes}
                              onChange={(v) => setField("pfgrossnotes", v)}
                              placeholder="Notes..."
                              disabled={!formData.pfgross || saving}
                            />
                          </div>
                        </Row>
                      </div>

                      {!formData.pfgross && (
                        <>
                          <div className="col-span-12 md:col-span-6">
                            <Row label="PF Split: Paid Basic">
                              <Check checked={formData.pfpb} onChange={(v) => setField("pfpb", v)} label="Paid Basic" />
                              <div className="mt-2 grid grid-cols-12 gap-2 items-center">
                                <div className="col-span-6">
                                  <Text
                                    type="number"
                                    value={formData.pfpbpercent}
                                    onChange={(v) => onPercent("pfpbpercent", v)}
                                    placeholder="0"
                                    disabled={!formData.pfpb || saving}
                                  />
                                </div>
                                <div className="col-span-6 text-sm">
                                  Test: <span className="font-semibold">{calcPercentValue(formData.pfpbpercent, formData.grosstest).toFixed(2)}</span>
                                </div>
                              </div>
                              <div className="mt-2">
                                <Text
                                  value={formData.pfpbnotes}
                                  onChange={(v) => setField("pfpbnotes", v)}
                                  placeholder="Notes..."
                                  disabled={!formData.pfpb || saving}
                                />
                              </div>
                            </Row>
                          </div>

                          <div className="col-span-12 md:col-span-6">
                            <Row label="PF Split: Conveyance">
                              <Check checked={formData.pfcon} onChange={(v) => setField("pfcon", v)} label="Conveyance" />
                              <div className="mt-2 grid grid-cols-12 gap-2 items-center">
                                <div className="col-span-6">
                                  <Text
                                    type="number"
                                    value={formData.pfconpercent}
                                    onChange={(v) => onPercent("pfconpercent", v)}
                                    placeholder="0"
                                    disabled={!formData.pfcon || saving}
                                  />
                                </div>
                                <div className="col-span-6 text-sm">
                                  Test: <span className="font-semibold">{calcPercentValue(formData.pfconpercent, formData.grosstest).toFixed(2)}</span>
                                </div>
                              </div>
                              <div className="mt-2">
                                <Text
                                  value={formData.pfconnotes}
                                  onChange={(v) => setField("pfconnotes", v)}
                                  placeholder="Notes..."
                                  disabled={!formData.pfcon || saving}
                                />
                              </div>
                            </Row>
                          </div>

                          <div className="col-span-12 md:col-span-6">
                            <Row label="PF Split: Educational">
                              <Check checked={formData.pfedu} onChange={(v) => setField("pfedu", v)} label="Educational" />
                              <div className="mt-2 grid grid-cols-12 gap-2 items-center">
                                <div className="col-span-6">
                                  <Text
                                    type="number"
                                    value={formData.pfedupercent}
                                    onChange={(v) => onPercent("pfedupercent", v)}
                                    placeholder="0"
                                    disabled={!formData.pfedu || saving}
                                  />
                                </div>
                                <div className="col-span-6 text-sm">
                                  Test: <span className="font-semibold">{calcPercentValue(formData.pfedupercent, formData.grosstest).toFixed(2)}</span>
                                </div>
                              </div>
                              <div className="mt-2">
                                <Text
                                  value={formData.pfedunotes}
                                  onChange={(v) => setField("pfedunotes", v)}
                                  placeholder="Notes..."
                                  disabled={!formData.pfedu || saving}
                                />
                              </div>
                            </Row>
                          </div>

                          <div className="col-span-12 md:col-span-6">
                            <Row label="PF Split: HRA">
                              <Check checked={formData.pfhra} onChange={(v) => setField("pfhra", v)} label="HRA" />
                              <div className="mt-2 grid grid-cols-12 gap-2 items-center">
                                <div className="col-span-6">
                                  <Text
                                    type="number"
                                    value={formData.pfhrapercent}
                                    onChange={(v) => onPercent("pfhrapercent", v)}
                                    placeholder="0"
                                    disabled={!formData.pfhra || saving}
                                  />
                                </div>
                                <div className="col-span-6 text-sm">
                                  Test: <span className="font-semibold">{calcPercentValue(formData.pfhrapercent, formData.grosstest).toFixed(2)}</span>
                                </div>
                              </div>
                              <div className="mt-2">
                                <Text
                                  value={formData.pfhranotes}
                                  onChange={(v) => setField("pfhranotes", v)}
                                  placeholder="Notes..."
                                  disabled={!formData.pfhra || saving}
                                />
                              </div>
                            </Row>
                          </div>

                          <div className="col-span-12 md:col-span-6">
                            <Row label="PF Percent from Total Value">
                              <Text
                                type="number"
                                value={formData.pftotpercent}
                                onChange={(v) => onPercent("pftotpercent", v)}
                                placeholder="0"
                                disabled={saving}
                              />
                              {errors.pftotpercent && (
                                <div className="text-red-400 text-xs mt-1">{errors.pftotpercent}</div>
                              )}
                              {errors.pfSplitTotal && (
                                <div className="text-red-400 text-xs mt-1">{errors.pfSplitTotal}</div>
                              )}
                            </Row>
                          </div>

                          <div className="col-span-12 md:col-span-6">
                            <Row label="PF Total Notes">
                              <Text
                                value={formData.pftotnotes}
                                onChange={(v) => setField("pftotnotes", v)}
                                placeholder="Notes..."
                                disabled={saving}
                              />
                            </Row>
                          </div>
                        </>
                      )}

                      <div className="col-span-12 md:col-span-6">
                        <Row label="PF Greater Value">
                          <Check
                            checked={formData.pfgreater}
                            onChange={(v) => setField("pfgreater", v)}
                            label="Greater Value"
                          />
                          <div className="mt-2 grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-6">
                              <Text
                                type="number"
                                value={formData.pfgreatervalue}
                                onChange={(v) => setField("pfgreatervalue", toNum(v))}
                                placeholder="0"
                                disabled={!formData.pfgreater || saving}
                              />
                            </div>
                            <div className="col-span-6 text-sm">
                              PF Test: <span className="font-semibold">{tests.pfFinal.toFixed(2)}</span>
                            </div>
                          </div>
                          <div className="mt-2">
                            <Text
                              value={formData.pfgreaternotes}
                              onChange={(v) => setField("pfgreaternotes", v)}
                              placeholder="Notes..."
                              disabled={!formData.pfgreater || saving}
                            />
                          </div>
                        </Row>
                      </div>
                    </>
                  )}
                </div>
              </Section>

              <Section title="ESI (Settings)">
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12">
                    <Row label="ESI Enable">
                      <Check checked={formData.esi} onChange={(v) => setField("esi", v)} label="Enable ESI" />
                    </Row>
                  </div>

                  {formData.esi && (
                    <>
                      <div className="col-span-12 md:col-span-6">
                        <Row label="ESI %">
                          <Text
                            type="number"
                            value={formData.esipercent}
                            onChange={(v) => onPercent("esipercent", v)}
                            placeholder="0"
                            disabled={saving}
                          />
                          {errors.esipercent && <div className="text-red-400 text-xs mt-1">{errors.esipercent}</div>}
                        </Row>
                      </div>

                      <div className="col-span-12 md:col-span-6">
                        <Row label="ESI Notes">
                          <Text value={formData.esinotes} onChange={(v) => setField("esinotes", v)} placeholder="Notes..." />
                        </Row>
                      </div>

                      <div className="col-span-12 md:col-span-6">
                        <Row label="ESI Base From Gross">
                          <Check
                            checked={formData.esigross}
                            onChange={(v) => setField("esigross", v)}
                            label="Gross"
                          />
                          <div className="mt-2 grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-6">
                              <Text
                                type="number"
                                value={formData.esigrosspercent}
                                onChange={(v) => onPercent("esigrosspercent", v)}
                                placeholder="0"
                                disabled={!formData.esigross || saving}
                              />
                              {errors.esigrosspercent && (
                                <div className="text-red-400 text-xs mt-1">{errors.esigrosspercent}</div>
                              )}
                            </div>
                            <div className="col-span-6 text-sm">
                              Base: <span className="font-semibold">{tests.esiBase.toFixed(2)}</span>
                            </div>
                          </div>
                          <div className="mt-2">
                            <Text
                              value={formData.esigrossnotes}
                              onChange={(v) => setField("esigrossnotes", v)}
                              placeholder="Notes..."
                              disabled={!formData.esigross || saving}
                            />
                          </div>
                        </Row>
                      </div>

                      {!formData.esigross && (
                        <>
                          <div className="col-span-12 md:col-span-6">
                            <Row label="ESI Split: Paid Basic">
                              <Check checked={formData.esipb} onChange={(v) => setField("esipb", v)} label="Paid Basic" />
                              <div className="mt-2 grid grid-cols-12 gap-2 items-center">
                                <div className="col-span-6">
                                  <Text
                                    type="number"
                                    value={formData.esipbpercent}
                                    onChange={(v) => onPercent("esipbpercent", v)}
                                    placeholder="0"
                                    disabled={!formData.esipb || saving}
                                  />
                                </div>
                                <div className="col-span-6 text-sm">
                                  Test:{" "}
                                  <span className="font-semibold">
                                    {calcPercentValue(formData.esipbpercent, formData.grosstest).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                              <div className="mt-2">
                                <Text
                                  value={formData.esipbnotes}
                                  onChange={(v) => setField("esipbnotes", v)}
                                  placeholder="Notes..."
                                  disabled={!formData.esipb || saving}
                                />
                              </div>
                            </Row>
                          </div>

                          <div className="col-span-12 md:col-span-6">
                            <Row label="ESI Split: Conveyance">
                              <Check checked={formData.esicon} onChange={(v) => setField("esicon", v)} label="Conveyance" />
                              <div className="mt-2 grid grid-cols-12 gap-2 items-center">
                                <div className="col-span-6">
                                  <Text
                                    type="number"
                                    value={formData.esiconpercent}
                                    onChange={(v) => onPercent("esiconpercent", v)}
                                    placeholder="0"
                                    disabled={!formData.esicon || saving}
                                  />
                                </div>
                                <div className="col-span-6 text-sm">
                                  Test:{" "}
                                  <span className="font-semibold">
                                    {calcPercentValue(formData.esiconpercent, formData.grosstest).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                              <div className="mt-2">
                                <Text
                                  value={formData.esiconnotes}
                                  onChange={(v) => setField("esiconnotes", v)}
                                  placeholder="Notes..."
                                  disabled={!formData.esicon || saving}
                                />
                              </div>
                            </Row>
                          </div>

                          <div className="col-span-12 md:col-span-6">
                            <Row label="ESI Split: Educational">
                              <Check checked={formData.esiedu} onChange={(v) => setField("esiedu", v)} label="Educational" />
                              <div className="mt-2 grid grid-cols-12 gap-2 items-center">
                                <div className="col-span-6">
                                  <Text
                                    type="number"
                                    value={formData.esiedupercent}
                                    onChange={(v) => onPercent("esiedupercent", v)}
                                    placeholder="0"
                                    disabled={!formData.esiedu || saving}
                                  />
                                </div>
                                <div className="col-span-6 text-sm">
                                  Test:{" "}
                                  <span className="font-semibold">
                                    {calcPercentValue(formData.esiedupercent, formData.grosstest).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                              <div className="mt-2">
                                <Text
                                  value={formData.esiedunotes}
                                  onChange={(v) => setField("esiedunotes", v)}
                                  placeholder="Notes..."
                                  disabled={!formData.esiedu || saving}
                                />
                              </div>
                            </Row>
                          </div>

                          <div className="col-span-12 md:col-span-6">
                            <Row label="ESI Split: HRA">
                              <Check checked={formData.esihra} onChange={(v) => setField("esihra", v)} label="HRA" />
                              <div className="mt-2 grid grid-cols-12 gap-2 items-center">
                                <div className="col-span-6">
                                  <Text
                                    type="number"
                                    value={formData.esihrapercent}
                                    onChange={(v) => onPercent("esihrapercent", v)}
                                    placeholder="0"
                                    disabled={!formData.esihra || saving}
                                  />
                                </div>
                                <div className="col-span-6 text-sm">
                                  Test:{" "}
                                  <span className="font-semibold">
                                    {calcPercentValue(formData.esihrapercent, formData.grosstest).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                              <div className="mt-2">
                                <Text
                                  value={formData.esihranotes}
                                  onChange={(v) => setField("esihranotes", v)}
                                  placeholder="Notes..."
                                  disabled={!formData.esihra || saving}
                                />
                              </div>
                            </Row>
                          </div>

                          <div className="col-span-12 md:col-span-6">
                            <Row label="ESI Percent from Total Value">
                              <Text
                                type="number"
                                value={formData.esitotpercent}
                                onChange={(v) => onPercent("esitotpercent", v)}
                                placeholder="0"
                                disabled={saving}
                              />
                              {errors.esitotpercent && (
                                <div className="text-red-400 text-xs mt-1">{errors.esitotpercent}</div>
                              )}
                              {errors.esiSplitTotal && (
                                <div className="text-red-400 text-xs mt-1">{errors.esiSplitTotal}</div>
                              )}
                            </Row>
                          </div>

                          <div className="col-span-12 md:col-span-6">
                            <Row label="ESI Total Notes">
                              <Text
                                value={formData.esitotnotes}
                                onChange={(v) => setField("esitotnotes", v)}
                                placeholder="Notes..."
                                disabled={saving}
                              />
                            </Row>
                          </div>
                        </>
                      )}

                      <div className="col-span-12 md:col-span-6">
                        <Row label="ESI Greater Value">
                          <Check
                            checked={formData.esigreater}
                            onChange={(v) => setField("esigreater", v)}
                            label="Greater Value"
                          />
                          <div className="mt-2 grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-6">
                              <Text
                                type="number"
                                value={formData.esigreatervalue}
                                onChange={(v) => setField("esigreatervalue", toNum(v))}
                                placeholder="0"
                                disabled={!formData.esigreater || saving}
                              />
                            </div>
                            <div className="col-span-6 text-sm">
                              ESI Test: <span className="font-semibold">{tests.esiFinal.toFixed(2)}</span>
                            </div>
                          </div>
                          <div className="mt-2">
                            <Text
                              value={formData.esigreaternotes}
                              onChange={(v) => setField("esigreaternotes", v)}
                              placeholder="Notes..."
                              disabled={!formData.esigreater || saving}
                            />
                          </div>
                        </Row>
                      </div>
                    </>
                  )}
                </div>
              </Section>

              <Section title="Other Options">
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12 md:col-span-4">
                    <Row label="Advance">
                      <Check checked={formData.advance} onChange={(v) => setField("advance", v)} label="Enable Advance" />
                    </Row>
                  </div>
                  <div className="col-span-12 md:col-span-4">
                    <Row label="Loan">
                      <Check checked={formData.loan} onChange={(v) => setField("loan", v)} label="Enable Loan" />
                    </Row>
                  </div>
                  <div className="col-span-12 md:col-span-4">
                    <Row label="TDS">
                      <Check checked={formData.tds} onChange={(v) => setField("tds", v)} label="Enable TDS" />
                    </Row>
                  </div>
                </div>
              </Section>

              <Section title="NetPay Settings">
                <Row label="Enable NetPay">
                  <Check checked={formData.netpay} onChange={(v) => setField("netpay", v)} label="Enable NetPay" />
                  <div className="mt-2 text-sm">
                    NetPay Test: <span className="font-semibold">{tests.netpayVal.toFixed(2)}</span>
                  </div>
                </Row>

                {formData.netpay && (
                  <div className="grid grid-cols-12 gap-3 mt-3">
                    <div className="col-span-12 md:col-span-6">
                      <div className={`p-3 rounded-lg ${isDark ? "bg-gray-800" : "bg-gray-50"} border ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                        <div className="font-semibold mb-2">Add</div>
                        <div className="flex flex-col gap-2">
                          <Check checked={formData.netpaypb} onChange={(v) => setField("netpaypb", v)} label="Paid Basic" />
                          <Check checked={formData.netpaycon} onChange={(v) => setField("netpaycon", v)} label="Conveyance" />
                          <Check checked={formData.netpayedu} onChange={(v) => setField("netpayedu", v)} label="Educational" />
                          <Check checked={formData.netpayhra} onChange={(v) => setField("netpayhra", v)} label="HRA" />
                        </div>
                      </div>
                    </div>

                    <div className="col-span-12 md:col-span-6">
                      <div className={`p-3 rounded-lg ${isDark ? "bg-gray-800" : "bg-gray-50"} border ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                        <div className="font-semibold mb-2">Minus</div>
                        <div className="flex flex-col gap-2">
                          <Check checked={formData.netpaypf} onChange={(v) => setField("netpaypf", v)} label="PF" />
                          <Check checked={formData.netpayesi} onChange={(v) => setField("netpayesi", v)} label="ESI" />
                          <Check checked={formData.netpayadvance} onChange={(v) => setField("netpayadvance", v)} label="Advance" />
                          <Check checked={formData.netpayloan} onChange={(v) => setField("netpayloan", v)} label="Loan" />
                          <Check checked={formData.netpaytds} onChange={(v) => setField("netpaytds", v)} label="TDS" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Section>

              {/* Actions */}
              <div className="flex justify-center gap-4 pb-6">
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                >
                  <FaCheck />
                  {saving ? "Saving..." : isEdit ? "Update" : "Save"}
                </button>

                <button
                  onClick={() => handleAddNew(false)}
                  disabled={saving}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                >
                  <FaPlus />
                  New
                </button>
              </div>
            </>
          )}

          {/* Table */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">S.No</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Scheme Name</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">PB%</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Conv%</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Edu%</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">HRA%</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">PF%</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">ESI%</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Enable</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Delete</th>
                    <th className="px-4 py-4 text-center font-semibold">Select</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan="11" className={`px-4 py-6 text-center text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        Loading...
                      </td>
                    </tr>
                  ) : records.length === 0 ? (
                    <tr>
                      <td colSpan="11" className={`px-4 py-6 text-center text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        No records found
                      </td>
                    </tr>
                  ) : (
                    records.map((r, index) => (
                      <tr
                        key={r.id}
                        className={`transition-colors duration-200 ${
                          index % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-gray-50") : isDark ? "bg-gray-900" : "bg-white"
                        }`}
                      >
                        <td className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.sNo}
                        </td>

                        <td className={`px-4 py-3 text-sm text-left border-r border-gray-200 dark:border-gray-700 font-medium ${isDark ? "text-blue-300" : "text-blue-600"}`}>
                          <button type="button" onClick={() => handleEditById(r.id)} className="underline hover:opacity-80" title="Click to edit">
                            {r.schemename}
                          </button>
                          {r.notes ? <div className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{r.notes}</div> : null}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {toNum(r.paidbasicpercent).toFixed(2)}
                        </td>
                        <td className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {toNum(r.conveyancepercent).toFixed(2)}
                        </td>
                        <td className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {toNum(r.educationalpercent).toFixed(2)}
                        </td>
                        <td className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {toNum(r.hrapercent).toFixed(2)}
                        </td>
                        <td className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {toNum(r.pfpercent).toFixed(2)}
                        </td>
                        <td className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {toNum(r.esipercent).toFixed(2)}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.enable ? "Yes" : "No"}
                        </td>

                        <td className="px-4 py-3 text-center border-r border-gray-200 dark:border-gray-700">
                          <button
                            type="button"
                            onClick={() => openDeleteOne(r.id)}
                            className={`px-3 py-1 rounded-lg text-white font-semibold ${
                              isDark ? "bg-red-700 hover:bg-red-600" : "bg-red-600 hover:bg-red-700"
                            }`}
                            title="Delete scheme"
                          >
                            Delete
                          </button>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={!!r.selected}
                            onChange={(e) => handleSelectRecord(r.id, e.target.checked)}
                            className={`w-4 h-4 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Remove selected */}
          <div className="flex justify-end mt-4">
            <button
              onClick={openRemoveSelected}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark ? "bg-red-700 hover:bg-red-600 text-white" : "bg-red-600 hover:bg-red-700 text-white"
              }`}
            >
              <FaTrash />
              Remove Selected
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-confirm-title"
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeDeleteConfirm}
          />

          <div
            className={`relative w-full max-w-md rounded-2xl border shadow-2xl overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-gray-900 to-gray-800 border-white/10 text-white"
                : "bg-white border-gray-200 text-gray-900"
            }`}
          >
            <div className={`h-1.5 w-full ${buttonClass}`} />

            <button
              type="button"
              onClick={closeDeleteConfirm}
              className={`absolute top-3 right-3 p-1.5 rounded-lg transition ${
                isDark ? "hover:bg-white/10 text-white/70" : "hover:bg-gray-100 text-gray-500"
              }`}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-600/15 text-red-500">
                  <FaExclamationTriangle className="text-xl" />
                </div>
                <div>
                  <h3 id="delete-confirm-title" className="text-lg font-semibold">
                    Delete Payroll Scheme
                  </h3>
                  <p className={`mt-2 text-sm leading-6 ${isDark ? "text-white/70" : "text-gray-600"}`}>
                    {deleteMode === "one" ? (
                      <>Would you like to remove this scheme? This action cannot be undone.</>
                    ) : (
                      <>
                        Are you sure you want to delete{" "}
                        <span className="font-semibold">
                          {pendingDeleteIds.length} selected scheme
                          {pendingDeleteIds.length > 1 ? "s" : ""}
                        </span>
                        ? This action cannot be undone.
                      </>
                    )}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeDeleteConfirm}
                  disabled={isDeleting}
                  className={`px-4 py-2 rounded-xl text-sm font-medium border transition ${
                    isDark
                      ? "border-white/15 bg-white/5 hover:bg-white/10 text-white"
                      : "border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700"
                  } disabled:opacity-50`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteSelected}
                  disabled={isDeleting}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 shadow transition hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                >
                  <FaTrash />
                  {isDeleting ? "Removing..." : "Remove"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayoutWrapper>
  );
};

export default PayrollCustomizationNew;